import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { updateReferralLiveStatus } from '@/lib/referralSystem';
import { debugLog } from '@/components/DebugConsole';
import { useAuth } from '@/contexts/FirebaseAuthContext';

const ArtistLiveStatusContext = createContext();

export const useArtistLiveStatus = () => {
  const context = useContext(ArtistLiveStatusContext);
  if (!context) {
    throw new Error('useArtistLiveStatus must be used within an ArtistLiveStatusProvider');
  }
  return context;
};

export const ArtistLiveStatusProvider = ({ children }) => {
  const [isLive, setIsLive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionEndTime, setSessionEndTime] = useState(null);
  const [currentVenue, setCurrentVenue] = useState('');
  const [performanceData, setPerformanceData] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Listen to Firestore live_status changes
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = onSnapshot(doc(db, 'live_status', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const wasLive = isLive;
        const nowLive = data.is_live;
        
        // Only trigger referral system when going live (not when going offline)
        if (!wasLive && nowLive) {
          debugLog('GO_LIVE', 'Live status changed via Firestore listener', { 
            userId: user.uid, 
            venue: data.venue || '',
            timestamp: new Date().toISOString()
          });
          
          setIsLive(true);
          setSessionStartTime(data.session_start_time?.toDate?.() || new Date());
          setSessionEndTime(data.session_end_time?.toDate?.() || null);
          setCurrentVenue(data.venue || '');
          setPerformanceData({
            startTime: data.session_start_time?.toDate?.() || new Date(),
            venue: data.venue || '',
            status: 'live'
          });
          
          // Update referral status when user goes live
          updateReferralLiveStatus(user.uid).then(() => {
            debugLog('SUCCESS', 'Live session started successfully via Firestore listener', { 
              userId: user.uid, 
              venue: data.venue || ''
            });
          }).catch(error => {
            console.error('Error updating referral live status:', error);
            debugLog('ERROR', 'Failed to update referral live status', { 
              userId: user.uid, 
              error: error.message 
            });
          });
        } else if (wasLive && !nowLive) {
          debugLog('GO_OFFLINE', 'Live status changed to offline via Firestore listener', { 
            timestamp: new Date().toISOString()
          });
          
          setIsLive(false);
          setSessionStartTime(null);
          setSessionEndTime(null);
          setCurrentVenue('');
          setPerformanceData(null);
          
          debugLog('SUCCESS', 'Live session ended successfully via Firestore listener');
        }
      } else {
        // Document doesn't exist, user is offline
        if (isLive) {
          debugLog('GO_OFFLINE', 'Live status document deleted via Firestore listener', { 
            timestamp: new Date().toISOString()
          });
          
          setIsLive(false);
          setSessionStartTime(null);
          setSessionEndTime(null);
          setCurrentVenue('');
          setPerformanceData(null);
          
          debugLog('SUCCESS', 'Live session ended successfully via Firestore listener');
        }
      }
    }, (error) => {
      console.error('Error listening to live status:', error);
    });

    return () => unsubscribe();
  }, [user?.uid, isLive]);

  const goLive = useCallback(async (venue = '', userId = null, duration = null) => {
    // CRITICAL: Check if artist is force-locked offline (e.g., /fitzsy)
    if (userId) {
      try {
        const liveStatusRef = doc(db, 'live_status', userId);
        const liveStatusDoc = await getDoc(liveStatusRef);
        
        if (liveStatusDoc.exists()) {
          const liveStatusData = liveStatusDoc.data();
          if (liveStatusData.force_keep_offline === true) {
            toast({
              title: "🚫 Cannot Go Live",
              description: "Your account has been locked offline. Please contact support if you need to go live.",
              variant: "destructive"
            });
            return false; // Prevent going live
          }
        }
      } catch (error) {
        console.error('Error checking force_keep_offline:', error);
        // Continue with normal flow if check fails
      }
      
      // CRITICAL: Check if user has songs in the database
      try {
        const songsQuery = query(
          collection(db, 'songs'), 
          where('user_id', '==', userId)
        );
        const songsSnapshot = await getDocs(songsQuery);
        const userSongs = songsSnapshot.docs.filter(doc => !doc.data().archived);
        
        if (userSongs.length === 0) {
          toast({
            title: "🚫 Cannot Go Live",
            description: "You MUST add songs to your catalogue before going live. No exceptions!",
            variant: "destructive"
          });
          
          // Trigger song wizard to add songs
          localStorage.setItem('ysr_show_song_wizard', 'true');
          return false; // Prevent going live
        }
      } catch (error) {
        console.error('Error checking songs:', error);
        toast({
          title: "Error",
          description: "Could not verify your song catalogue. Please try again.",
          variant: "destructive"
        });
        return false;
      }
      
      // Manual payout mode: only confirm profile exists before going live.
      try {
        console.log('💳 Checking profile status for manual payouts...');
        const profileRef = doc(db, 'profiles', userId);
        const profileDoc = await getDoc(profileRef);
        
        if (!profileDoc.exists()) {
          toast({
            title: "🚫 Cannot Go Live",
            description: "Profile not found. Please contact support.",
            variant: "destructive",
          });
          return false; // Prevent going live
        }
      } catch (error) {
        console.error('❌ Error checking profile status (non-blocking):', error);
        console.warn('⚠️ Proceeding with go live in manual payout mode');
      }
    }
    
    // Only log if this is called directly (not from LiveStatusControls)
    if (userId) {
      debugLog('GO_LIVE', 'Starting live session via ArtistLiveStatusContext', { 
        userId, 
        venue,
        duration,
        timestamp: new Date().toISOString()
      });
    }

    const sessionStartTime = new Date();
    const sessionEndTime = duration ? new Date(sessionStartTime.getTime() + (duration * 60 * 1000)) : null;
    
    // Update Firestore document first
    if (userId) {
      try {
        console.log('🔍 DEBUG: Attempting to update Firestore document for user:', userId);
        console.log('🔍 DEBUG: Document data:', {
          is_live: true,
          session_start_time: sessionStartTime,
          session_end_time: sessionEndTime,
          venue: venue || '',
          updated_at: new Date()
        });
        
        await setDoc(doc(db, 'live_status', userId), {
          is_live: true,
          session_start_time: sessionStartTime,
          session_end_time: sessionEndTime,
          venue: venue || '',
          updated_at: new Date()
        });
        
        console.log('✅ DEBUG: Firestore document updated successfully');
        
        // Update local state
        setIsLive(true);
        setSessionStartTime(sessionStartTime);
        setSessionEndTime(sessionEndTime);
        setCurrentVenue(venue);
        setPerformanceData({
          startTime: sessionStartTime,
          venue: venue,
          status: 'live'
        });
        
        console.log('✅ DEBUG: Local state updated successfully');
        
        // Update referral status when user goes live
        await updateReferralLiveStatus(userId);
        console.log('✅ DEBUG: Referral status updated successfully');
        
        debugLog('SUCCESS', 'Live session started successfully via ArtistLiveStatusContext', { 
          userId, 
          venue,
          duration
        });
      } catch (error) {
        console.error('❌ ERROR: Failed to start live session:', error);
        console.error('❌ ERROR: Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack
        });
        debugLog('ERROR', 'Failed to start live session', { 
          userId, 
          duration,
          error: error.message 
        });
        return false;
      }
    }
    
    return true; // Allow going live
  }, [toast]);

  const goOffline = useCallback(async () => {
    debugLog('GO_OFFLINE', 'Ending live session via ArtistLiveStatusContext', { 
      timestamp: new Date().toISOString()
    });

    // Update Firestore document first
    if (user?.uid) {
      try {
        await setDoc(doc(db, 'live_status', user.uid), {
          is_live: false,
          session_start_time: null,
          session_end_time: null,
          venue: '',
          updated_at: new Date()
        });
        
        // Update local state
        setIsLive(false);
        setSessionStartTime(null);
        setSessionEndTime(null);
        setCurrentVenue('');
        setPerformanceData(null);

        debugLog('SUCCESS', 'Live session ended successfully via ArtistLiveStatusContext');
      } catch (error) {
        console.error('Error ending live session:', error);
        debugLog('ERROR', 'Failed to end live session', { 
          error: error.message 
        });
      }
    }
  }, [user?.uid]);

  const updatePerformanceData = useCallback((updates) => {
    setPerformanceData(prev => ({
      ...prev,
      ...updates,
      lastUpdated: new Date()
    }));
  }, []);

  const getSessionDuration = useCallback(() => {
    if (!sessionStartTime) return 0;
    return Math.floor((new Date() - sessionStartTime) / 1000);
  }, [sessionStartTime]);

  const formatSessionDuration = useCallback(() => {
    const duration = getSessionDuration();
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [getSessionDuration]);

  const value = {
    isLive,
    sessionStartTime,
    sessionEndTime,
    setSessionEndTime,
    currentVenue,
    performanceData,
    goLive,
    goOffline,
    updatePerformanceData,
    getSessionDuration,
    formatSessionDuration,
  };

  return (
    <ArtistLiveStatusContext.Provider value={value}>
      {children}
    </ArtistLiveStatusContext.Provider>
  );
};
